// src/components/OSINT/AccountDetail.js
//
// Per-account leak provenance view. Reached by clicking an account name in the
// OSINT > Leaks finding list. Aggregates everything Intelligence X has on one
// selector across every search: a chronological leak-trace timeline, a Wasabi
// Shinobi AI narrative of how the data spread, and the account's findings.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertCircle,
  GitBranch,
  Loader,
  Search as SearchIcon,
  SearchCheck,
  Sparkles,
  X,
} from 'lucide-react';
import { osintAPI } from '../../services/api';
import PageHeader from '../common/PageHeader';
import LeakTrace from './LeakTrace';
import { FindingCard } from './Leaks';

const SEVERITY_RANK = { critical: 5, suspected: 4.5, high: 4, medium: 3, low: 2, info: 1, noise: 0 };

const SEVERITY_CHIP = {
  critical:  'text-red-700 bg-red-100 border-red-300',
  suspected: 'text-rose-700 bg-rose-100 border-rose-300',
  high:     'text-orange-700 bg-orange-100 border-orange-300',
  medium:   'text-yellow-700 bg-yellow-100 border-yellow-300',
  low:      'text-blue-700 bg-blue-100 border-blue-300',
  info:     'text-slate-700 bg-slate-100 border-slate-300',
  noise:    'text-slate-500 bg-slate-100 border-slate-200',
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
};

const AccountDetail = () => {
  // react-router v6 already decodes path params, so accountParam is the plain
  // email (e.g. "jshields@wasabi.com").
  const { account: accountParam } = useParams();
  const account = accountParam || '';

  const [trace, setTrace] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, f] = await Promise.all([
        osintAPI.getAccountTrace(account),
        osintAPI.listFindings({ account, include_noise: true }),
      ]);
      setTrace(t.data);
      const list = (f.data || [])
        .slice()
        .sort((a, b) => (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0));
      setFindings(list);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load account trace');
    } finally {
      setLoading(false);
    }
  }, [account]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFindingChange = (updated) => {
    setFindings((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
  };

  const handleDeepSearch = async (finding) => {
    try {
      const r = await osintAPI.deepSearchFinding(finding.id);
      setToast({
        kind: 'success',
        message: `Deep search launched (search #${r.data.id}). This trace will refresh in ~12s.`,
      });
      setTimeout(() => load(), 12000);
      setTimeout(() => setToast(null), 12000);
    } catch (err) {
      setToast({ kind: 'error', message: err?.response?.data?.detail || 'Deep search failed' });
      setTimeout(() => setToast(null), 6000);
    }
  };

  const rollup = trace?.severity_rollup || {};
  const rollupEntries = Object.entries(rollup).sort(
    (a, b) => (SEVERITY_RANK[b[0]] ?? 0) - (SEVERITY_RANK[a[0]] ?? 0),
  );

  return (
    <div>
      <PageHeader
        title={account || 'Account'}
        subtitle="Leak provenance trace · Powered by intelx.io · Wasabi Shinobi AI"
        icon={GitBranch}
        showBackButton
        actions={
          findings.length > 0 && (
            <button
              onClick={() => handleDeepSearch(findings[0])}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-wasabi-300 bg-wasabi-100 text-wasabi-700 hover:bg-wasabi-100 transition-colors text-sm"
              title="Re-run an Intelligent search for this account (burns IntelX credits)"
            >
              <SearchCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Deep search again</span>
            </button>
          )
        }
      />

      <div className="px-6 py-6 space-y-6">
        {loading && (
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-16">
            <Loader className="w-4 h-4 animate-spin" />
            <span>Reconstructing leak provenance...</span>
          </div>
        )}

        {error && !loading && (
          <div className="cyber-card rounded-lg p-4 border border-danger-red/40 bg-danger-red/5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-danger-red flex-shrink-0 mt-0.5" />
            <p className="text-danger-red text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && trace && (
          <>
            {/* Summary row */}
            <div className="rounded-2xl border border-cyber-300/40 bg-cyber-100/30 p-5">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-mono text-slate-900 break-all">{trace.account}</span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-cyber-300/40 text-slate-500">
                  {trace.account_kind}
                </span>
                <div className="flex-1" />
                {rollupEntries.length > 0 ? (
                  rollupEntries.map(([sev, count]) => (
                    <span
                      key={sev}
                      className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                        SEVERITY_CHIP[sev] || SEVERITY_CHIP.info
                      }`}
                    >
                      {count} {sev}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-slate-500">No graded leak events</span>
                )}
              </div>
              <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
                <span>{trace.findings_count} finding{trace.findings_count === 1 ? '' : 's'}</span>
                <span>·</span>
                <span>{trace.nodes_count} leak event{trace.nodes_count === 1 ? '' : 's'} on the timeline</span>
                <span>·</span>
                <span>tracked {fmtDate(trace.first_seen_at)} &rarr; {fmtDate(trace.last_seen_at)}</span>
              </div>
            </div>

            {/* Provenance trace timeline */}
            <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden">
              <div className="px-5 py-3 border-b border-cyber-300/30 flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-shinobi-green" />
                <h3 className="text-sm font-semibold text-slate-900">Leak provenance timeline</h3>
                <span className="text-[11px] text-slate-500">
                  how this account's data propagated, oldest first
                </span>
              </div>
              <div className="p-5">
                <LeakTrace nodes={trace.nodes} />
              </div>
            </div>

            {/* AI narrative */}
            {trace.narrative && (
              <div className="rounded-2xl border border-shinobi-purple/30 bg-shinobi-purple/5 overflow-hidden">
                <div className="px-5 py-3 border-b border-shinobi-purple/20 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-shinobi-purple" />
                  <h3 className="text-sm font-semibold text-slate-900">Wasabi Shinobi AI analysis</h3>
                </div>
                <p className="px-5 py-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {trace.narrative}
                </p>
              </div>
            )}

            {/* Findings for this account */}
            <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden">
              <div className="px-5 py-3 border-b border-cyber-300/30 flex items-center gap-2">
                <SearchIcon className="w-4 h-4 text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-900">Findings</h3>
                <span className="text-[11px] text-slate-500">({findings.length})</span>
              </div>
              {findings.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">
                  No findings recorded for this account.
                </div>
              ) : (
                <div className="divide-y divide-cyber-300/20">
                  {findings.map((f) => (
                    <FindingCard
                      key={f.id}
                      finding={f}
                      onChange={handleFindingChange}
                      onDeepSearch={handleDeepSearch}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {toast && (
          <div
            className={`fixed bottom-6 right-6 z-50 max-w-md rounded-xl border px-4 py-3 shadow-lg flex items-start gap-3 ${
              toast.kind === 'error'
                ? 'border-red-300 bg-red-100 text-red-700'
                : 'border-wasabi-300 bg-wasabi-100 text-wasabi-700'
            }`}
            style={{ backdropFilter: 'blur(6px)' }}
          >
            {toast.kind === 'error' ? (
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            ) : (
              <SearchCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
            )}
            <p className="flex-1 text-xs">{toast.message}</p>
            <button onClick={() => setToast(null)} className="text-slate-500 hover:text-slate-900">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountDetail;
